/**
 * NeonWordmark — Yan-Vada (යන්-වාද) neon SVG wordmark.
 *
 * Ported from Yan-Vada Hero Pilot/components/neon-logos.jsx (Session 15b Claude Design).
 * Sinhala-only, traced from the actual set neon sign.
 *
 * Per-glyph live flicker with warm-up animation. Real neon doesn't flicker in unison.
 * Placeholder trace (typographic-as-neon) until Ridma delivers the hand-drawn SVG.
 * When Ridma's SVG arrives, replace the glyph markup in NeonGlyphs only —
 * the filter + flicker primitives stay.
 *
 * Exports:
 *   <NeonWordmark />           — transparent in-hero wordmark (drop-in)
 *   <NeonLogoExplorations />   — labelled panel for the design system
 */

// Source color mapping from brand/reference/neon (SG1-D9 production plane):
//   ය   red     (#FF1F3A)
//   න්  green   (#3DFF6E)
//   -    white   (#F5F5F5)
//   වා  purple  (#B84DFF)
//   ද   orange  (#FF8A1F)
const NEON_CHUNKS: Array<{ t: string; c: string }> = [
  { t: 'ය',  c: '#FF1F3A' },
  { t: 'න්', c: '#3DFF6E' },
  { t: '-',   c: '#F5F5F5' },
  { t: 'වා', c: '#B84DFF' },
  { t: 'ද',  c: '#FF8A1F' },
]

// Approximate em-advances per chunk for manual horizontal layout
// (eyeballed against Noto Sans Sinhala at weight 500).
const CHUNK_ADVANCES = [0.95, 1.15, 0.55, 1.25, 0.95]
const SPACING = 0.94

// ---- Shared filter primitive: neon tube glow with inner highlight + ambient halo.
// Tuned to look like a glass tube lit with gas, not a text-shadow effect.
const NeonDefs = ({ id }: { id: string }) => (
  <defs>
    <filter id={`${id}-glow`} x="-60%" y="-80%" width="220%" height="260%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="0.6" result="inner" />
      <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="halo1" />
      <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="halo2" />
      <feGaussianBlur in="SourceGraphic" stdDeviation="20" result="wash" />
      <feColorMatrix in="halo2" type="matrix"
        values="1.4 0 0 0 0  0 1.4 0 0 0  0 0 1.4 0 0  0 0 0 1 0" result="halo2Bright" />
      <feMerge>
        <feMergeNode in="wash" />
        <feMergeNode in="wash" />
        <feMergeNode in="halo2Bright" />
        <feMergeNode in="halo2Bright" />
        <feMergeNode in="halo1" />
        <feMergeNode in="halo1" />
        <feMergeNode in="inner" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
    <radialGradient id={`${id}-panel`} cx="50%" cy="50%" r="75%">
      <stop offset="0%" stopColor="#0a0a0a" />
      <stop offset="100%" stopColor="#000000" />
    </radialGradient>
  </defs>
)

// ---- Per-tube flicker stylesheet. Every glyph flickers on its own rhythm.
const FLICKER_CSS = (id: string) => `
  @keyframes neon-flicker-a {
    0%, 100%   { opacity: 1; }
    42%        { opacity: 0.15; }
    43%        { opacity: 1; }
    48%        { opacity: 0.4; }
    48.5%      { opacity: 1; }
    88%        { opacity: 0.2; }
    88.8%      { opacity: 1; }
  }
  @keyframes neon-flicker-b {
    0%, 100%   { opacity: 1; }
    14.2%      { opacity: 0.3; }
    14.6%      { opacity: 1; }
    62.2%      { opacity: 0.1; }
    62.6%      { opacity: 1; }
  }
  @keyframes neon-flicker-c {
    0%, 100%   { opacity: 1; }
    27.3%      { opacity: 0.2; }
    27.7%      { opacity: 1; }
    72.2%      { opacity: 0.5; }
    72.5%      { opacity: 1; }
  }
  @keyframes neon-warmup {
    0%   { opacity: 0; }
    1%   { opacity: 0.8; }
    2%   { opacity: 0.3; }
    4%   { opacity: 0.9; }
    6%   { opacity: 0.4; }
    9%   { opacity: 1; }
    10%  { opacity: 0.6; }
    13%  { opacity: 1; }
  }
  [data-neon="${id}"] .flicker { animation-fill-mode: both; animation-iteration-count: infinite; }
  [data-neon="${id}"] .flicker-0 { animation: neon-warmup 800ms 0ms 1 both, neon-flicker-a 5.3s 900ms infinite; }
  [data-neon="${id}"] .flicker-1 { animation: neon-warmup 600ms 200ms 1 both, neon-flicker-b 6.1s 1200ms infinite; }
  [data-neon="${id}"] .flicker-2 { animation: neon-warmup 400ms 80ms 1 both, neon-flicker-c 7.7s 1600ms infinite; }
  [data-neon="${id}"] .flicker-3 { animation: neon-warmup 700ms 300ms 1 both, neon-flicker-a 6.9s 2400ms infinite reverse; }
  [data-neon="${id}"] .flicker-4 { animation: neon-warmup 900ms 120ms 1 both, neon-flicker-b 5.9s 800ms infinite; }
`

const LiveFlickerStyle = ({ id }: { id: string }) => (
  // eslint-disable-next-line react/no-danger
  <style dangerouslySetInnerHTML={{ __html: FLICKER_CSS(id) }} />
)

// ---- Glyph layout helper. Returns absolute x-coords for each chunk center.
function chunkPositions(fontSize: number, cx: number): number[] {
  const total = CHUNK_ADVANCES.reduce((a, b) => a + b, 0)
  const totalWidthEm = total * SPACING
  const positions: number[] = []
  let cursor = -totalWidthEm / 2
  for (let i = 0; i < NEON_CHUNKS.length; i++) {
    const advance = CHUNK_ADVANCES[i] * SPACING
    positions.push(cx + (cursor + advance / 2) * fontSize)
    cursor += advance
  }
  return positions
}

// ---- Reusable glyph row.
const NeonGlyphs = ({
  fontSize,
  cx,
  cy,
  filterId,
}: {
  fontSize: number
  cx: number
  cy: number
  filterId: string
}) => {
  const xs = chunkPositions(fontSize, cx)
  return (
    <>
      {NEON_CHUNKS.map((ch, i) => (
        <text
          key={i}
          className={`flicker flicker-${i}`}
          data-lockup-part={ch.t === '-' ? 'yan-vada-hyphen' : undefined}
          data-neon-chunk={ch.t === '-' ? 'hyphen' : `glyph-${i}`}
          x={xs[i]}
          y={cy}
          textAnchor="middle"
          fontFamily="var(--font-sinhala)"
          fontSize={fontSize}
          fontWeight={500}
          paintOrder="stroke"
          stroke={ch.c}
          strokeWidth={3}
          strokeLinejoin="round"
          strokeLinecap="round"
          fill="rgba(255,255,255,0.12)"
          filter={`url(#${filterId}-glow)`}
        >
          {ch.t}
        </text>
      ))}
    </>
  )
}

// ---- The one canonical exploration panel (for the design system / reference page).
export const NeonLogoExplorations = () => {
  const id = 'neon-sys'
  return (
    <div style={{
      maxWidth: 1280, margin: '0 auto', padding: '24px',
      display: 'flex', justifyContent: 'center',
    }}>
      <div data-neon={id} style={{
        display: 'flex', flexDirection: 'column', gap: 12,
        width: '100%', maxWidth: 720,
      }}>
        <div style={{
          position: 'relative',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#050505',
          boxShadow: '0 24px 60px -20px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.04)',
        }}>
          <svg viewBox="0 0 800 320" width="100%" style={{ display: 'block' }}>
            <NeonDefs id={id} />
            <rect width="800" height="320" fill={`url(#${id}-panel)`} />
            {/* horizontal mount rails — top/bottom, suggesting the physical fixture */}
            <rect x="0" y="0" width="800" height="14" fill="rgba(255,255,255,0.04)" />
            <rect x="0" y="306" width="800" height="14" fill="rgba(255,255,255,0.04)" />
            <g className="flicker-root">
              <NeonGlyphs fontSize={150} cx={400} cy={200} filterId={id} />
            </g>
          </svg>
          <LiveFlickerStyle id={id} />
        </div>
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          color: '#c2ae91', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase',
        }}>
          <span style={{ color: '#e8dcc4', fontWeight: 600 }}>Yan-Vada · neon wordmark</span>
          <span style={{ color: '#887462' }}>Live flicker · traced from set</span>
        </div>
      </div>
    </div>
  )
}

// ---- In-hero wordmark. Drop-in: no panel, no rails, transparent canvas.
// Height controls rendered size; id must be unique per instance on the page.
interface NeonWordmarkProps {
  height?: number | string
  id?: string
  clipOverflow?: boolean
}

const NeonWordmark = ({ height = 130, id = 'neon-hero', clipOverflow = false }: NeonWordmarkProps) => (
  <div data-neon={id} style={{ display: 'inline-block', height, lineHeight: 0, overflow: clipOverflow ? 'hidden' : undefined }}>
    <svg
      viewBox="0 0 800 320"
      width="auto"
      height={height}
      style={{ display: 'block', overflow: clipOverflow ? 'hidden' : 'visible' }}
      aria-label="Yan-Vada — යන්-වාද"
      role="img"
    >
      <NeonDefs id={id} />
      <g className="flicker-root">
        <NeonGlyphs fontSize={150} cx={400} cy={205} filterId={id} />
      </g>
    </svg>
    <LiveFlickerStyle id={id} />
  </div>
)

export default NeonWordmark
